import type { FilterState } from '@/components/filterable-search-bar/type'
import { allowsMultipleConditions } from './groupedFilterFields'
import type { FilterGroup, FilterKey, GroupJoiner } from './types'

let idCounter = 0
/** Shared with URL restore, which re-keys a group carrying no id. */
export const buildGroupId = (): string => {
	idCounter += 1
	return `grp-${idCounter}-${Date.now().toString(36)}`
}

/** How an incoming condition folds into the conditions a group already holds. */
export type ConditionCommitMode =
	/** One chip per key: replace any condition on the same field. */
	| 'merge'
	/** Numeric key: keep what is there and add another comparison. */
	| 'append'
	/** Numeric key, same panel session: refine the comparison it added. */
	| 'replaceLast'

const lastIndexForField = (conditions: FilterState[], field: string): number => {
	for (let i = conditions.length - 1; i >= 0; i--) {
		if (conditions[i].field === field) {
			return i
		}
	}
	return -1
}

/** 'merge' replaces by field: the value picker preselects existing values on reopen, so incoming already
 *  carries the user's complete selection rather than an addition. */
const foldCondition = (conditions: FilterState[], incoming: FilterState, mode: ConditionCommitMode): FilterState[] => {
	if (mode === 'append') {
		return [...conditions, incoming]
	}
	const index =
		mode === 'replaceLast'
			? lastIndexForField(conditions, incoming.field)
			: conditions.findIndex(c => c.field === incoming.field)
	if (index === -1) {
		return [...conditions, incoming]
	}
	const next = [...conditions]
	next[index] = incoming
	return next
}

/**
 * Mode for a condition committed from an add-filter menu. On a numeric key the panel's first apply adds a
 * comparison and later ones refine it — without that split, its auto-apply would stack a chip per apply.
 */
export const commitModeForField = (
	field: FilterKey,
	options: { enableDataTypeOperators?: boolean; isNewExpression?: boolean },
): ConditionCommitMode => {
	if (!allowsMultipleConditions(field, options.enableDataTypeOperators)) {
		return 'merge'
	}
	return options.isNewExpression ? 'append' : 'replaceLast'
}

export const createGroup = (condition: FilterState): FilterGroup => ({
	id: buildGroupId(),
	operator: 'and',
	joiner: 'or',
	conditions: [condition],
})

export const addConditionToGroup = (
	groups: FilterGroup[],
	groupId: string,
	condition: FilterState,
	mode: ConditionCommitMode = 'merge',
): FilterGroup[] =>
	groups.map(g => (g.id === groupId ? { ...g, conditions: foldCondition(g.conditions, condition, mode) } : g))

export const updateGroupCondition = (
	groups: FilterGroup[],
	groupId: string,
	conditionId: string,
	condition: FilterState,
): FilterGroup[] =>
	groups.map(g =>
		g.id === groupId ? { ...g, conditions: g.conditions.map(c => (c.id === conditionId ? condition : c)) } : g,
	)

/** Removes the condition and prunes the group if it becomes empty. */
export const removeGroupCondition = (groups: FilterGroup[], groupId: string, conditionId: string): FilterGroup[] =>
	groups
		.map(g => (g.id === groupId ? { ...g, conditions: g.conditions.filter(c => c.id !== conditionId) } : g))
		.filter(g => g.conditions.length > 0)

export const removeGroup = (groups: FilterGroup[], groupId: string): FilterGroup[] =>
	groups.filter(group => group.id !== groupId)

export const splitGroupAtCondition = (
	groups: FilterGroup[],
	groupId: string,
	conditionId: string,
	joiner: GroupJoiner,
): FilterGroup[] =>
	groups.flatMap(group => {
		if (group.id !== groupId) {
			return [group]
		}
		const splitIndex = group.conditions.findIndex(condition => condition.id === conditionId)
		if (splitIndex <= 0) {
			return [group]
		}
		return [
			{ ...group, conditions: group.conditions.slice(0, splitIndex) },
			{
				...group,
				id: buildGroupId(),
				joiner,
				conditions: group.conditions.slice(splitIndex),
			},
		]
	})

export const setGroupJoiner = (groups: FilterGroup[], groupId: string, joiner: GroupJoiner): FilterGroup[] =>
	groups.map(g => (g.id === groupId ? { ...g, joiner } : g))
